import { useState, useCallback } from 'react';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { Resolution } from '../../../Entities';
import { isBrokerAgent } from '../../../../../utils/brokerAgentUtils';
import { AuthenticatedUser } from '../../../../../../../shared/types';

type UseDeliveryOptionsArgs = {
    orderId: number;
    user: AuthenticatedUser;
};

type UseDeliveryOptionsResult = {
    downloadingResolution: 'high' | 'normal' | null;
    downloadLink: string | null;
    isLinkModalOpen: boolean;
    showError: boolean;
    getDownloadLink: (resolution: Resolution) => void;
    downloadHighResolutionImages: () => void;
    downloadNormalResolutionImages: () => void;
    closeLinkModal: () => void;
    closeError: () => void;
};

export function useDeliveryOptions({ orderId, user }: UseDeliveryOptionsArgs): UseDeliveryOptionsResult {
    var [downloadingResolution, setDownloadingResolution] = useState<'high' | 'normal' | null>(null);
    var [downloadLink, setDownloadLink] = useState<string | null>(null);
    var [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    var [showError, setShowError] = useState(false);

    var getDownloadLink = useCallback((resolution: Resolution) => {
        setIsLinkModalOpen(true);
        setDownloadLink(null);

        ImageProcessingApi
            .getZipDownloadLink(orderId, resolution)
            .then(function (result) {
                setDownloadLink(result.downloadLink);
            })
            .catch(function () {
                setIsLinkModalOpen(false);
                setShowError(true);
            });
    }, [orderId]);

    var downloadHighResolutionImages = useCallback(() => {
        setDownloadingResolution('high');

        var isBroker = isBrokerAgent(user.details.role);

        ImageProcessingApi
            .downloadEditedImages(orderId, false, isBroker)
            .then(function () {
                setDownloadingResolution(null);
            })
            .catch(function () {
                setDownloadingResolution(null);
                setShowError(true);
            });
    }, [orderId, user]);

    var downloadNormalResolutionImages = useCallback(() => {
        setDownloadingResolution('normal');

        var isBroker = isBrokerAgent(user.details.role);

        ImageProcessingApi
            .downloadEditedImages(orderId, true, isBroker)
            .then(function () {
                setDownloadingResolution(null);
            })
            .catch(function () {
                setDownloadingResolution(null);
                setShowError(true);
            });
    }, [orderId, user]);

    var closeError = useCallback(function () {
        setShowError(false);
    }, []);

    var closeLinkModal = useCallback(function () {
        setIsLinkModalOpen(false);
        setDownloadLink(null);
    }, []);

    return {
        downloadingResolution,
        downloadLink,
        isLinkModalOpen,
        showError,
        getDownloadLink,
        downloadHighResolutionImages,
        downloadNormalResolutionImages,
        closeLinkModal,
        closeError
    };
}
